import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import styled from '@emotion/styled';
import Checkbox from '../../common/Checkbox';
import { fetchStatisticsOfUsingChestnyznak } from './../../../actions/items';
import { getVariantTitle } from '../../../helpers';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  margin-top: 15px;

  .chz-info {
    margin: 15px 0;
  }

  .spent-chz {
    margin-left: 30px;
  }
`;

const ChestnyZnakInfoBlock = (props) => {
  const {
    handleChangeInput,
    allowSkipCis,
    currentProductVariants,
    currentProductId,
    isProductPage,
    variantId,
  } = props;

  const skus = useSelector((state) => state.items.statisticsOfUsingChestnyznak);
  const warehouses = useSelector((state) => state.orders.warehouses);
  const features = useSelector((state) => state.items.features);

  const { t } = useAutoTranslation();

  const dispatch = useDispatch();

  const [variantsWithDeficit, setVariantsWithDeficit] = useState([]);
  const [itemDeficitText, setItemDeficitText] = useState(null);
  const [variantsWithChzData, setVariantsWithChzData] = useState(null);

  const isSingleProduct = currentProductVariants?.length <= 1;

  const displayVarinatsWithChzDataComponent = (skus) => {
    const varinatsWithChz = skus?.map((s) => {
      const linkToVariant = `/items/${currentProductId}/variants/${s.sku_id}`;
      const freeChz = s.serial_numbers[0].free;
      const usedChz = s.serial_numbers[0].used;

      if (freeChz || usedChz) {
        let chzInfoText = '';

        if (freeChz) chzInfoText += `${t('Свободных ЧЗ в пуле')}: ${freeChz} `;
        if (usedChz) chzInfoText += `${t('Израсходованных ЧЗ')}: ${usedChz} `;
        return (
          <p className="mt-1">
            {t('Вариант')}
            <Link to={linkToVariant} key={s.sku_id}>
              <u className="ml-1">{`${s.variantTitle || s.sku_id}`}</u>. &nbsp;
            </Link>
            {chzInfoText}
          </p>
        );
      }
    });

    setVariantsWithChzData(varinatsWithChz);
  };

  const getStatisticsOfUsingChestnyznak = async (variantId) => {
    await dispatch(fetchStatisticsOfUsingChestnyznak([variantId]));
  };

  const addDataToStocks = (currentProductVariants) => {
    const productStocksUpdated = [];

    currentProductVariants.forEach((variant) => {
      variant.stock.forEach((stock) => {
        const variantTitle = getVariantTitle(variant, features);
        stock['variantTitle'] = variantTitle;
        stock['variantId'] = variant.id;
        productStocksUpdated.push(stock);
      });
    });

    warehouses.forEach((warehouse) => {
      productStocksUpdated.forEach((stock) => {
        if (warehouse.code === stock.warehouseCode) {
          stock['isDefault'] = warehouse.isDefault;
          stock['goodsCondition'] = warehouse.goodsCondition;
          stock['title'] = warehouse.title;
        }
      });
    });

    return productStocksUpdated;
  };

  const getSkusWithChestnyZnak = (skus) => {
    return skus.filter((sku) => sku.serial_numbers[0].code === 'chestniy_znak');
  };

  const handleItemDeficit = (chestnyZnakSkus, productStocksUpdated) => {
    const variantsWithDeficit = [];

    chestnyZnakSkus.forEach((sku, idx) => {
      productStocksUpdated.forEach((stock) => {
        const freeChz = sku.serial_numbers[0].free;
        const haveDeficitChz = sku.sku_id === stock.variantId && freeChz < stock.stockAvailable;
        const currentVariantId = stock.variantId;
        const currentVariantTitle = stock.variantTitle;

        if (sku.sku_id === currentVariantId) {
          chestnyZnakSkus[idx]['variantTitle'] = currentVariantTitle;
        }

        if (haveDeficitChz && stock.isDefault && stock.goodsCondition === 'NORMAL') {
          const deficitQty = stock.stockAvailable - freeChz;
          setItemDeficitText(
            `${t('Возможна нехватка')} ${deficitQty} ${t('ЧЗ для свободных остатков на складе')} ${
              stock.title
            }`,
          );

          variantsWithDeficit.push(currentVariantId);
        }
      });
    });

    setVariantsWithDeficit(variantsWithDeficit);
    return chestnyZnakSkus;
  };

  useEffect(() => {
    if (!isProductPage) {
      getStatisticsOfUsingChestnyznak(variantId);
    }
  }, []);

  useEffect(() => {
    if (warehouses && currentProductVariants && skus) {
      const productStocksUpdated = addDataToStocks(currentProductVariants);
      const chestnyZnakSkus = getSkusWithChestnyZnak(skus);

      const updatedChzSkus = handleItemDeficit(chestnyZnakSkus, productStocksUpdated);

      if (!isSingleProduct) displayVarinatsWithChzDataComponent(chestnyZnakSkus);
    }
  }, [warehouses, currentProductVariants, skus, features]);

  const freeChZInPoolQty = skus?.[0]?.serial_numbers?.[0]?.free;
  const spentChZQty = skus?.[0]?.serial_numbers?.[0]?.used;

  return (
    <Wrapper>
      {!isProductPage ? (
        <h4 className="header-title">{t('Учёт товара по уникальным номерам при отгрузке')}</h4>
      ) : null}

      {isProductPage ? (
        <Checkbox
          label={t('Разрешена отгрузка без ЧЗ')}
          id="allowSkipCis"
          checked={allowSkipCis}
          onChange={(e) => handleChangeInput('allowSkipCis', e.target.checked)}
        />
      ) : null}

      {(isProductPage && isSingleProduct) || !isProductPage ? (
        <div className="chz-info">
          {freeChZInPoolQty ? (
            <span>
              {t('Свободных ЧЗ в пуле')}:<b>&nbsp;{freeChZInPoolQty}</b>
            </span>
          ) : null}
          {spentChZQty ? (
            <span className={freeChZInPoolQty ? 'spent-chz' : ''}>
              {t('Израсходованных ЧЗ')}:<b>&nbsp;{spentChZQty}</b>
            </span>
          ) : null}
        </div>
      ) : (
        <div className="chz-info">
          {t('Данные о ЧЗ в вариантах')}:{variantsWithChzData}
        </div>
      )}

      {/* Displays on the product page and variant page */}
      {itemDeficitText && (
        <div class="alert alert-warning warning mt-2">
          <i class="mdi mdi-alert-outline mr-2"></i>
          {itemDeficitText}
        </div>
      )}

      {/* Displays only on the product page */}
      {isProductPage && variantsWithDeficit.length > 0 ? (
        <div class="alert alert-warning warning mt-2">
          <i class="mdi mdi-alert-outline mr-2"></i>
          {t('В вариантах')} &nbsp;
          {variantsWithDeficit.map((variant) => {
            const linkToVariant = `/items/${currentProductId}/variants/${variant}`;
            return (
              <Link to={linkToVariant} key={variant}>
                <u>{variant}</u>&nbsp;
              </Link>
            );
          })}
          {t('возможна нехватка ЧЗ для свободных остатков')}
        </div>
      ) : null}
    </Wrapper>
  );
};

export default ChestnyZnakInfoBlock;
